import { createContext } from './core/context.js';
import { mountShell } from './ui/shell.js';
import { listSamples, getSample } from './core/sample-store.js';
import { bindShortcuts } from './core/shortcuts.js';
import { mountShortcutsPanel } from './ui/shortcuts-panel.js';

import drums from './modules/inst-drums.js';
import synth from './modules/inst-synth.js';
import piano from './modules/inst-piano.js';
import sampler from './modules/inst-sampler.js';
import fxReverb from './modules/fx-reverb.js';
import fxDelay from './modules/fx-delay.js';
import inputMidi from './modules/input-midi.js';

const ctx = createContext();

// Render the UI shell first so the start-overlay is visible immediately.
// Components defer their own mount until 'app:started' fires.
mountShell(ctx, document.getElementById('app'));

// Register all feature modules. Order matters: FX channels must exist before
// instruments so the mixer panel can render send knobs for them.
// To add a new feature later (a new instrument, effect, panel, etc.), drop a
// file under src/modules/ and add it to this list.
[fxReverb, fxDelay, drums, synth, piano, sampler, inputMidi].forEach(m => ctx.addModule(m));

// Hydrate any persisted samples from IndexedDB. Keys look like "drum:kick"
// or "sampler:c4" — the bit before the colon picks the instrument id.
// Audio-clip keys use the prefix "clip:<id>" and are hydrated separately.
ctx.bus.on('app:started', async () => {
    try {
        const entries = await listSamples();
        for (const e of entries) {
            // Skip audio-clip blobs here — they're loaded by the audio-track
            // hydration step below once the song restores its track list.
            if (e.key.startsWith('clip:')) continue;
            const data = await getSample(e.key);
            if (!data?.blob) continue;
            const [type, id] = e.key.split(':');
            const instId = type === 'drum' ? id : type;
            const inst = ctx.instruments.get(instId);
            if (!inst?.loadSample) continue;
            try { await inst.loadSample(data.blob, data.name, { persist: false }); }
            catch (err) { console.warn('failed to hydrate', e.key, err); }
        }
    } catch (err) { console.warn('sample hydration skipped:', err.message); }
});

// Restore Tone.Players for audio tracks/clips after the song state is
// rehydrated (either at boot or after a project load).
async function hydrateAudioTracks() {
    const tracks = ctx.song.getAudioTracks();
    for (const track of tracks) {
        if (!ctx.mixer.get(track.channelId)) {
            ctx.mixer.addChannel({ id: track.channelId, name: track.name, color: '#5fa8d3' });
        }
        for (const clip of track.clips) {
            if (ctx.audioClips.has(clip.id)) continue;
            try {
                const data = await getSample(clip.bufferKey);
                if (!data?.blob) continue;
                const ab = await (await data.blob.arrayBuffer());
                const audioBuffer = await ctx.Tone.context.decodeAudioData(ab.slice(0));
                await ctx.audioClips.addClip(clip.id, audioBuffer, track.channelId);
            } catch (err) { console.warn('audio clip hydrate failed:', clip.id, err); }
        }
    }
}
ctx.bus.on('app:started', () => { hydrateAudioTracks(); });
ctx.bus.on('audio:tracksRestored', () => { hydrateAudioTracks(); });

// Install the scheduler tick that walks the active pattern (or song
// arrangement) and triggers each track's instrument at the right step.
ctx.bus.on('app:started', () => {
    // Cache the duration of one 16th note in seconds; recompute when BPM changes.
    let sixteenthSec = 60 / 120 / 4;
    const recalc = () => { sixteenthSec = 60 / ctx.scheduler.getBPM() / 4; };
    recalc();
    ctx.bus.on('transport:bpmChanged', recalc);

    // Song-mode counter — total 16th-note count since playback started.
    // The clip-based timeline maps each step to a bar position, then to a
    // step within whichever clip covers that bar.
    let songStepCounter = 0;
    const STEPS_PER_BAR = 16;
    ctx.bus.on('transport:started', () => { songStepCounter = 0; ctx.song.setCursorBar(0); });
    ctx.bus.on('transport:stopped', () => {
        songStepCounter = 0;
        ctx.song.setCursorBar(0);
        ctx.bus.emit('arrangement:position', 0);
        // Cut off any audio clips that were mid-playback when the user stopped.
        try { ctx.audioClips.stopAll(ctx.Tone.now()); } catch (_) {}
    });

    ctx.scheduler.setTickHandler((stepIndex, time) => {
        let pattern;
        let stepInPattern;
        const inSong = ctx.song.getMode() === 'song';

        if (inSong) {
            const bpm = ctx.scheduler.getBPM();
            // Step-precise total so fractional pattern clips (e.g. an 8-step
            // pattern that's half a bar long) loop at the right length.
            const totalSteps = ctx.song.totalStepsIncludingAudio(bpm);
            if (totalSteps === 0) {
                ctx.bus.emit('rack:clearPlayhead');
                return;
            }
            const absoluteStep = songStepCounter % totalSteps;
            const absoluteBar = Math.floor(absoluteStep / STEPS_PER_BAR);
            const stepInBar = absoluteStep % STEPS_PER_BAR;
            ctx.song.setCursorBar(absoluteBar);
            ctx.bus.emit('arrangement:position', absoluteStep / STEPS_PER_BAR);

            // Audio clips can land at any 16th-step (left-trim shifts startBar
            // sub-bar). Each step we ask the song for clips starting *here*.
            const audioClipsHere = ctx.song.getAudioClipsAtStep(absoluteStep);
            for (const c of audioClipsHere) {
                ctx.audioClips.trigger(c.id, time, {
                    offset: c.offsetSec || 0,
                    duration: Number.isFinite(c.playSec) ? c.playSec : c.durationSec,
                    fadeIn: c.fadeInSec || 0,
                    fadeOut: c.fadeOutSec || 0,
                });
            }

            // Find the pattern clip whose absolute step range covers this
            // step — handles 0.5-bar / 1-bar / 2-bar clips uniformly.
            const clip = ctx.song.findClipAtStep(absoluteStep);
            songStepCounter += 1;
            if (!clip) {
                ctx.bus.emit('rack:clearPlayhead');
                return;
            }
            pattern = ctx.patterns.list().find(p => p.id === clip.patternId);
            if (!pattern) {
                ctx.bus.emit('rack:clearPlayhead');
                return;
            }
            const stepInClip = absoluteStep - Math.round(clip.startBar * STEPS_PER_BAR);
            stepInPattern = stepInClip % pattern.length;
        } else {
            pattern = ctx.patterns.getActive();
            if (!pattern) return;
            stepInPattern = stepIndex % pattern.length;
        }

        for (const [trackKey, track] of Object.entries(pattern.tracks)) {
            const v = track.steps[stepInPattern];
            if (!v) continue;
            // Per-step probability — roll once per active hit.
            const prob = (track.probabilities && track.probabilities[stepInPattern]) ?? 1;
            if (prob < 1 && Math.random() > prob) continue;
            const len = (track.lengths && track.lengths[stepInPattern]) || 1;
            // Pitched lanes get the variable duration; drums ignore it.
            const dur = len > 1 ? sixteenthSec * len : '16n';
            const [instId] = trackKey.split(':');
            const inst = ctx.instruments.get(instId);
            inst?.play(track.note ?? null, time, dur, v);
        }

        // Only animate the rack playhead when the visible pattern is the one
        // currently sounding; otherwise the user gets a confusing ghost playhead.
        const visibleId = ctx.patterns.getActiveId();
        if (!inSong || pattern.id === visibleId) {
            ctx.bus.emit('rack:playhead', stepInPattern);
        } else {
            ctx.bus.emit('rack:clearPlayhead');
        }
    });
    ctx.bus.on('transport:stopped', () => ctx.bus.emit('rack:clearPlayhead'));
});

// Cmd/Ctrl+Z = undo · Cmd/Ctrl+Shift+Z (or Ctrl+Y) = redo. Suppress when
// the user is typing into a real text input or select.
document.addEventListener('keydown', (e) => {
    const meta = e.metaKey || e.ctrlKey;
    if (!meta) return;
    const tag = document.activeElement?.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA') return;
    const key = e.key.toLowerCase();
    if (key === 'z' && !e.shiftKey) { e.preventDefault(); ctx.history.undo(); }
    else if ((key === 'z' && e.shiftKey) || key === 'y') { e.preventDefault(); ctx.history.redo(); }
});

// Mount the shortcut cheat-sheet and bind global shortcuts.
const shortcutsPanel = mountShortcutsPanel();
bindShortcuts(ctx, shortcutsPanel);

// Expose for console-driven scripting / experimentation.
window.app = ctx;
